import React, { useState } from "react";
import {
  MapPin,
  Battery,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Download,
  Plus,
  ClipboardList,
  Eye,
  Zap,
  Calendar,
  User,
  Info,
  X,
  ChevronDown,
} from "lucide-react";
import { useTenant } from "../contexts/TenantContext";

// ── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_AEDS = [
  {
    id: "AED-001",
    serial: "PHL-FRX-2021-4471",
    model: "Philips HeartStart FRx",
    location: "Station 1 — Main Lobby",
    gps: { lat: "27.9506° N", lng: "82.4572° W" },
    batteryExpiry: "2026-08-15",
    padsExpiry: "2026-09-01",
    lastInspected: "2026-01-10",
    inspector: "J. Martinez",
    status: "Pass",
    notes: "Unit in excellent condition. Tested and functional.",
    history: [
      {
        date: "2026-01-10",
        inspector: "J. Martinez",
        result: "Pass",
        notes: "All systems nominal.",
      },
      {
        date: "2025-07-15",
        inspector: "R. Thompson",
        result: "Pass",
        notes: "Battery and pads within range.",
      },
      {
        date: "2025-01-08",
        inspector: "J. Martinez",
        result: "Warning",
        notes: "Battery at 72%, flagged for replacement within 6 months.",
      },
    ],
  },
  {
    id: "AED-002",
    serial: "ZLL-AED3-2020-9982",
    model: "Zoll AED 3",
    location: "Engine Bay — Bay 2",
    gps: { lat: "27.9488° N", lng: "82.4601° W" },
    batteryExpiry: "2026-03-20",
    padsExpiry: "2026-04-10",
    lastInspected: "2026-02-01",
    inspector: "S. Okafor",
    status: "Warning",
    notes: "Battery and pads expiring within 60 days. Schedule replacement.",
    history: [
      {
        date: "2026-02-01",
        inspector: "S. Okafor",
        result: "Warning",
        notes: "Battery and pads approaching expiry.",
      },
      {
        date: "2025-08-10",
        inspector: "J. Martinez",
        result: "Pass",
        notes: "Full inspection passed.",
      },
      {
        date: "2025-02-14",
        inspector: "S. Okafor",
        result: "Pass",
        notes: "No issues found.",
      },
    ],
  },
  {
    id: "AED-003",
    serial: "CAR-HS1-2019-3310",
    model: "Cardiac Science Powerheart G5",
    location: "Training Room B",
    gps: { lat: "27.9515° N", lng: "82.4558° W" },
    batteryExpiry: "2025-11-01",
    padsExpiry: "2025-10-15",
    lastInspected: "2025-10-05",
    inspector: "R. Thompson",
    status: "Fail",
    notes:
      "Battery and pads EXPIRED. Unit removed from service. Replacement ordered.",
    history: [
      {
        date: "2025-10-05",
        inspector: "R. Thompson",
        result: "Fail",
        notes: "Battery and pads expired. Unit out of service.",
      },
      {
        date: "2025-04-20",
        inspector: "J. Martinez",
        result: "Warning",
        notes: "Expiry dates imminent.",
      },
      {
        date: "2024-10-12",
        inspector: "S. Okafor",
        result: "Pass",
        notes: "All checks passed.",
      },
    ],
  },
  {
    id: "AED-004",
    serial: "DEF-9300P-2022-7741",
    model: "Defibtech Lifeline VIEW",
    location: "Apparatus Bay — Wall Mount",
    gps: { lat: "27.9499° N", lng: "82.4590° W" },
    batteryExpiry: "2027-05-30",
    padsExpiry: "2027-04-15",
    lastInspected: "2026-02-28",
    inspector: "J. Martinez",
    status: "Pass",
    notes: "Recently inspected. All readings nominal.",
    history: [
      {
        date: "2026-02-28",
        inspector: "J. Martinez",
        result: "Pass",
        notes: "New unit. All checks passed.",
      },
      {
        date: "2025-09-01",
        inspector: "S. Okafor",
        result: "Pass",
        notes: "Quarterly inspection complete.",
      },
      {
        date: "2025-03-10",
        inspector: "R. Thompson",
        result: "Pass",
        notes: "Initial deployment inspection.",
      },
    ],
  },
  {
    id: "AED-005",
    serial: "PHL-FRX-2020-5523",
    model: "Philips HeartStart FRx",
    location: "Admin Office — Reception",
    gps: { lat: "27.9522° N", lng: "82.4545° W" },
    batteryExpiry: "2026-06-10",
    padsExpiry: "2026-05-20",
    lastInspected: "2026-01-22",
    inspector: "S. Okafor",
    status: "Warning",
    notes: "Pads expiring within 90 days. Schedule replacement.",
    history: [
      {
        date: "2026-01-22",
        inspector: "S. Okafor",
        result: "Warning",
        notes: "Pads nearing expiry.",
      },
      {
        date: "2025-07-30",
        inspector: "J. Martinez",
        result: "Pass",
        notes: "All systems check out.",
      },
      {
        date: "2025-01-15",
        inspector: "R. Thompson",
        result: "Pass",
        notes: "No issues.",
      },
    ],
  },
];

// Mock GPS pin positions (relative % positions in the map area)
const MAP_PINS = [
  { id: "AED-001", x: 30, y: 45, status: "Pass" },
  { id: "AED-002", x: 55, y: 30, status: "Warning" },
  { id: "AED-003", x: 72, y: 60, status: "Fail" },
  { id: "AED-004", x: 42, y: 68, status: "Pass" },
  { id: "AED-005", x: 18, y: 25, status: "Warning" },
];

// ── Status Helpers ────────────────────────────────────────────────────────────

function statusConfig(status) {
  switch (status) {
    case "Pass":
      return {
        bg: "bg-lime-100",
        text: "text-lime-900",
        border: "border-lime-300",
        icon: <CheckCircle2 size={13} className="text-lime-700" />,
        pinColor: "var(--color-success)",
      };
    case "Warning":
      return {
        bg: "bg-yellow-100",
        text: "text-yellow-900",
        border: "border-yellow-300",
        icon: <AlertTriangle size={13} className="text-yellow-700" />,
        pinColor: "var(--color-warn)",
      };
    case "Fail":
      return {
        bg: "bg-red-100",
        text: "text-red-900",
        border: "border-red-300",
        icon: <XCircle size={13} className="text-red-700" />,
        pinColor: "var(--color-danger)",
      };
    default:
      return {
        bg: "bg-zinc-100",
        text: "text-zinc-700",
        border: "border-zinc-300",
        icon: null,
        pinColor: "#71717a",
      };
  }
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const diff = (new Date(dateStr) - new Date()) / 86400000;
  return diff >= 0 && diff <= 90;
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function DateCell({ dateStr }) {
  if (isExpired(dateStr)) {
    return (
      <span className="inline-flex items-center gap-1 text-red-700 font-semibold text-xs">
        {dateStr}
      </span>
    );
  }
  if (isExpiringSoon(dateStr)) {
    return (
      <span className="inline-flex items-center gap-1 text-yellow-700 font-semibold text-xs">
        {dateStr}
      </span>
    );
  }
  return <span className="text-zinc-600 text-xs">{dateStr}</span>;
}

function StatusBadge({ status }) {
  const cfg = statusConfig(status);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.icon}
      {status}
    </span>
  );
}

// ── GPS Map Section ───────────────────────────────────────────────────────────

function GpsMapSection({ onPinClick }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg mb-4 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-center gap-2">
          <MapPin size={15} className="text-zinc-500" />
          <span className="text-xs font-bold text-zinc-600 uppercase tracking-widest">
            GPS Deployment Map
          </span>
          <span className="text-xs text-zinc-400 font-normal normal-case tracking-normal ml-1">
            — Google My Maps / GPS Sync
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-lime-500 inline-block" />{" "}
            Pass
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />{" "}
            Warning
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{" "}
            Fail
          </span>
        </div>
      </div>

      {/* Map canvas */}
      <div className="relative bg-zinc-200 h-36 w-full overflow-hidden">
        {/* Subtle grid lines to suggest a map */}
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#c4c4c4"
                strokeWidth="0.5"
              />
            </pattern>
            <pattern
              id="roads-h"
              width="120"
              height="120"
              patternUnits="userSpaceOnUse"
            >
              <line
                x1="0"
                y1="60"
                x2="120"
                y2="60"
                stroke="#b0b0b0"
                strokeWidth="2.5"
              />
            </pattern>
            <pattern
              id="roads-v"
              width="120"
              height="120"
              patternUnits="userSpaceOnUse"
            >
              <line
                x1="60"
                y1="0"
                x2="60"
                y2="120"
                stroke="#b0b0b0"
                strokeWidth="2.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          <rect width="100%" height="100%" fill="url(#roads-h)" />
          <rect width="100%" height="100%" fill="url(#roads-v)" />
          {/* Block shapes to suggest buildings */}
          <rect
            x="80"
            y="20"
            width="50"
            height="30"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
          <rect
            x="200"
            y="55"
            width="70"
            height="20"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
          <rect
            x="350"
            y="15"
            width="40"
            height="45"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
          <rect
            x="500"
            y="70"
            width="60"
            height="25"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
          <rect
            x="150"
            y="85"
            width="35"
            height="35"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
          <rect
            x="620"
            y="30"
            width="55"
            height="28"
            fill="var(--color-hairline-cloud)"
            stroke="var(--color-ink-4)"
            strokeWidth="0.5"
            rx="1"
          />
        </svg>

        {/* Map pins */}
        {MAP_PINS.map((pin) => {
          const cfg = statusConfig(pin.status);
          return (
            <button
              key={pin.id}
              onClick={() => onPinClick(pin.id)}
              className="absolute -translate-x-1/2 -translate-y-full group"
              style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              title={`${pin.id} — ${pin.status}`}
            >
              <div className="flex flex-col items-center">
                <div
                  className="w-7 h-7 rounded-full border-2 border-white shadow-md flex items-center justify-center text-white"
                  style={{ backgroundColor: cfg.pinColor }}
                >
                  <Zap size={12} />
                </div>
                <div
                  className="w-0.5 h-2"
                  style={{ backgroundColor: cfg.pinColor }}
                />
                {/* Tooltip on hover */}
                <span className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-zinc-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none">
                  {pin.id}
                </span>
              </div>
            </button>
          );
        })}

        {/* Watermark label */}
        <div className="absolute bottom-2 right-3 text-zinc-400 text-xs flex items-center gap-1 select-none">
          <MapPin size={11} />
          County Fire Dept — Agency AED Locations
        </div>
      </div>
    </div>
  );
}

// ── Log Inspection Modal ──────────────────────────────────────────────────────

function LogInspectionModal({ open, onClose, aeds }) {
  const [form, setForm] = useState({
    deviceId: "",
    batteryExpiry: "",
    padsExpiry: "",
    inspectorName: "",
    notes: "",
    status: "",
  });
  const [submitted, setSubmitted] = useState(false);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    // TODO: POST to /api/v1/equipment/inspections
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm({
        deviceId: "",
        batteryExpiry: "",
        padsExpiry: "",
        inspectorName: "",
        notes: "",
        status: "",
      });
      onClose();
    }, 1800);
  }

  if (!open) return null;

  const STATUS_OPTIONS = [
    {
      value: "Pass",
      label: "Pass",
      style: "bg-lime-100 border-lime-400 text-lime-900 hover:bg-lime-200",
      icon: <CheckCircle2 size={18} />,
    },
    {
      value: "Warning",
      label: "Warning",
      style:
        "bg-yellow-100 border-yellow-400 text-yellow-900 hover:bg-yellow-200",
      icon: <AlertTriangle size={18} />,
    },
    {
      value: "Fail",
      label: "Fail",
      style: "bg-red-100 border-red-400 text-red-900 hover:bg-red-200",
      icon: <XCircle size={18} />,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-lg shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-zinc-600" />
            <span className="font-bold text-zinc-900 text-base">
              Log AED Inspection
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body — large tap targets for mobile */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {/* Device selector — full width, large touch target */}
            <div>
              <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                Select Device *
              </label>
              <div className="relative">
                <select
                  required
                  value={form.deviceId}
                  onChange={(e) => handleChange("deviceId", e.target.value)}
                  className="w-full appearance-none bg-white border-2 border-zinc-200 rounded-lg px-4 py-4 text-base font-medium text-zinc-900 focus:outline-none focus:border-zinc-600 pr-10"
                >
                  <option value="">— Choose an AED unit —</option>
                  {aeds.map((aed) => (
                    <option key={aed.id} value={aed.id}>
                      {aed.id} — {aed.location}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Inspector name */}
            <div>
              <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                Inspector Name *
              </label>
              <div className="relative">
                <User
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <input
                  required
                  type="text"
                  placeholder="Full name"
                  value={form.inspectorName}
                  onChange={(e) =>
                    handleChange("inspectorName", e.target.value)
                  }
                  className="w-full border-2 border-zinc-200 rounded-lg pl-10 pr-4 py-4 text-base font-medium text-zinc-900 focus:outline-none focus:border-zinc-600 bg-white"
                />
              </div>
            </div>

            {/* Date inputs — native HTML for mobile keyboards */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                  Battery Expiry *
                </label>
                <div className="relative">
                  <Calendar
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
                  />
                  <input
                    required
                    type="date"
                    value={form.batteryExpiry}
                    onChange={(e) =>
                      handleChange("batteryExpiry", e.target.value)
                    }
                    className="w-full border-2 border-zinc-200 rounded-lg pl-9 pr-2 py-4 text-base font-medium text-zinc-900 focus:outline-none focus:border-zinc-600 bg-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                  Pads Expiry *
                </label>
                <div className="relative">
                  <Calendar
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
                  />
                  <input
                    required
                    type="date"
                    value={form.padsExpiry}
                    onChange={(e) => handleChange("padsExpiry", e.target.value)}
                    className="w-full border-2 border-zinc-200 rounded-lg pl-9 pr-2 py-4 text-base font-medium text-zinc-900 focus:outline-none focus:border-zinc-600 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Status — large block buttons, not a dropdown */}
            <div>
              <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                Inspection Result *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange("status", opt.value)}
                    className={`flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg border-2 font-bold text-sm transition-all ${opt.style} ${
                      form.status === opt.value
                        ? "ring-2 ring-offset-1 ring-zinc-600 scale-[1.02]"
                        : ""
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">
                Notes
              </label>
              <textarea
                rows={3}
                placeholder="Optional inspection notes, observations, or action items…"
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                className="w-full border-2 border-zinc-200 rounded-lg px-4 py-3 text-base text-zinc-900 focus:outline-none focus:border-zinc-600 bg-white resize-none"
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={
                !form.deviceId ||
                !form.status ||
                !form.inspectorName ||
                submitted
              }
              className="w-full py-4 rounded-lg bg-yellow-400 text-zinc-950 font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-500 active:scale-[0.98] transition-all"
            >
              {submitted ? "Submitting…" : "Submit Inspection Record"}
            </button>
          </form>
        </div>

        {/* Footer note */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-start gap-2">
          <Info size={14} className="text-zinc-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-zinc-500">
            Submitting will send a confirmation email to the agency admin and
            update the AED profile.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── AED Profile Modal ─────────────────────────────────────────────────────────

function AedProfileModal({ open, aed, onClose }) {
  if (!open || !aed) return null;

  const cfg = statusConfig(aed.status);

  function historyResultStyle(result) {
    switch (result) {
      case "Pass":
        return "text-lime-700 bg-lime-50 border-lime-200";
      case "Warning":
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "Fail":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-zinc-600 bg-zinc-50 border-zinc-200";
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-zinc-600" />
            <span className="font-bold text-zinc-900 text-base">
              {aed.id} — AED Profile
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Current status banner */}
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${cfg.bg} ${cfg.border}`}
          >
            <div className="flex-shrink-0">
              {statusConfig(aed.status).icon &&
                React.cloneElement(statusConfig(aed.status).icon, { size: 20 })}
            </div>
            <div>
              <div className={`font-bold text-sm ${cfg.text}`}>
                Current Status: {aed.status}
              </div>
              <div className={`text-xs ${cfg.text} opacity-80 mt-0.5`}>
                {aed.notes}
              </div>
            </div>
          </div>

          {/* Device details grid */}
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
              <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
                Device Details
              </span>
            </div>
            <div className="divide-y divide-zinc-100">
              {[
                { label: "Device ID", value: aed.id },
                { label: "Serial Number", value: aed.serial },
                { label: "Model", value: aed.model },
                { label: "Location", value: aed.location },
                { label: "GPS", value: `${aed.gps.lat}, ${aed.gps.lng}` },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-start px-4 py-2.5 gap-4"
                >
                  <span className="text-xs text-zinc-400 font-medium w-28 flex-shrink-0 pt-0.5">
                    {row.label}
                  </span>
                  <span className="text-sm text-zinc-800 font-semibold">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Expiry details */}
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
              <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
                Expiry Dates
              </span>
            </div>
            <div className="divide-y divide-zinc-100">
              {[
                {
                  label: "Battery Expiry",
                  value: aed.batteryExpiry,
                  date: true,
                },
                { label: "Pads Expiry", value: aed.padsExpiry, date: true },
                { label: "Last Inspected", value: aed.lastInspected },
                { label: "Inspector", value: aed.inspector },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center px-4 py-2.5 gap-4"
                >
                  <span className="text-xs text-zinc-400 font-medium w-28 flex-shrink-0">
                    {row.label}
                  </span>
                  {row.date ? (
                    <DateCell dateStr={row.value} />
                  ) : (
                    <span className="text-sm text-zinc-800 font-semibold">
                      {row.value}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Inspection History — last 3 */}
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
                Inspection History
              </span>
              <span className="text-xs text-zinc-400">Last 3 checks</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {(aed.history || []).map((entry, idx) => (
                <div key={idx} className="px-4 py-3 flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex-shrink-0 px-2 py-0.5 rounded border text-xs font-bold ${historyResultStyle(entry.result)}`}
                  >
                    {entry.result}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-zinc-800">
                        {entry.date}
                      </span>
                      <span className="text-xs text-zinc-400">
                        by {entry.inspector}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      {entry.notes}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-white border-2 border-zinc-200 text-zinc-700 font-semibold text-sm hover:bg-zinc-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function EquipmentManagementPage() {
  const { user } = useTenant();

  const [inspectionModalOpen, setInspectionModalOpen] = useState(false);
  const [profileModalAed, setProfileModalAed] = useState(null);

  if (
    !user ||
    (!user.roles.includes("AGENCY_ADMIN") &&
      !user.roles.includes("GLOBAL_ADMIN") &&
      !user.roles.includes("ADMIN"))
  ) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-900 text-sm">
        You do not have permission to view this page.
      </div>
    );
  }

  const passCount = MOCK_AEDS.filter((a) => a.status === "Pass").length;
  const warnCount = MOCK_AEDS.filter((a) => a.status === "Warning").length;
  const failCount = MOCK_AEDS.filter((a) => a.status === "Fail").length;

  return (
    <div className="bg-zinc-50 min-h-full">
      <div className="max-w-[1200px] mx-auto">
        {/* ── Mock Data Banner ── */}
        <div className="flex items-start gap-2.5 px-4 py-3 mb-4 bg-amber-50 border border-amber-300 rounded-lg">
          <Info size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900">
            <span className="font-bold">Demo data only.</span> All AED records,
            GPS coordinates, inspection history, and statuses on this page are
            hardcoded mock data for UI visualization purposes. No equipment data
            is currently stored in or retrieved from the database. Backend
            integration is pending.
          </p>
        </div>

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-zinc-900 tracking-tight">
              Agency Equipment (AEDs)
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              County Fire Dept · {MOCK_AEDS.length} units tracked
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-zinc-700 text-sm font-semibold hover:bg-zinc-50 transition-colors">
              <Download size={14} />
              Export Report
            </button>
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-200 bg-white text-zinc-700 text-sm font-semibold hover:bg-zinc-50 transition-colors">
              <Plus size={14} />
              Add New AED
            </button>
            <button
              onClick={() => setInspectionModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yellow-400 text-zinc-950 text-sm font-bold hover:bg-yellow-500 transition-colors"
            >
              <ClipboardList size={14} />
              Log Inspection
            </button>
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-3 gap-0 border border-zinc-200 rounded-lg overflow-hidden bg-white mb-4">
          <div className="px-4 py-3 border-r border-zinc-200 border-t-4 border-t-lime-500">
            <div className="text-2xl font-bold text-lime-700">{passCount}</div>
            <div className="text-xs text-zinc-500 font-medium mt-0.5 flex items-center gap-1">
              <CheckCircle2 size={11} className="text-lime-600" /> Pass
            </div>
          </div>
          <div className="px-4 py-3 border-r border-zinc-200 border-t-4 border-t-yellow-400">
            <div className="text-2xl font-bold text-yellow-700">
              {warnCount}
            </div>
            <div className="text-xs text-zinc-500 font-medium mt-0.5 flex items-center gap-1">
              <AlertTriangle size={11} className="text-yellow-600" /> Warning
            </div>
          </div>
          <div className="px-4 py-3 border-t-4 border-t-red-500">
            <div className="text-2xl font-bold text-red-700">{failCount}</div>
            <div className="text-xs text-zinc-500 font-medium mt-0.5 flex items-center gap-1">
              <XCircle size={11} className="text-red-600" /> Fail / Expired
            </div>
          </div>
        </div>

        {/* ── GPS Map ── */}
        <GpsMapSection
          onPinClick={(id) => {
            const aed = MOCK_AEDS.find((a) => a.id === id);
            if (aed) setProfileModalAed(aed);
          }}
        />

        {/* ── AED Table ── */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
              <Battery size={13} className="text-zinc-400" />
              AED Inventory — {MOCK_AEDS.length} Units
            </span>
            <span className="text-xs text-zinc-400">
              Agency: County Fire Dept
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-zinc-300">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Device ID &amp; Location
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    GPS Coordinates
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Battery Expiry
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Pads Expiry
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Last Inspected
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-widest whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {MOCK_AEDS.map((aed, idx) => {
                  const rowBg =
                    aed.status === "Fail"
                      ? "bg-red-50"
                      : aed.status === "Warning"
                        ? "bg-yellow-50/50"
                        : "";

                  return (
                    <tr
                      key={aed.id}
                      className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${rowBg}`}
                    >
                      {/* Device ID & Location */}
                      <td className="px-4 py-3">
                        <div className="font-bold text-zinc-900 text-xs font-mono">
                          {aed.id}
                        </div>
                        <div className="text-zinc-500 text-xs mt-0.5 flex items-center gap-1">
                          <MapPin
                            size={10}
                            className="text-zinc-400 flex-shrink-0"
                          />
                          {aed.location}
                        </div>
                      </td>

                      {/* GPS */}
                      <td className="px-4 py-3">
                        <div className="text-xs text-zinc-500 font-mono leading-relaxed">
                          {aed.gps.lat}
                          <br />
                          {aed.gps.lng}
                        </div>
                      </td>

                      {/* Battery Expiry */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Battery
                            size={12}
                            className={
                              isExpired(aed.batteryExpiry)
                                ? "text-red-500"
                                : isExpiringSoon(aed.batteryExpiry)
                                  ? "text-yellow-500"
                                  : "text-zinc-400"
                            }
                          />
                          <DateCell dateStr={aed.batteryExpiry} />
                        </div>
                      </td>

                      {/* Pads Expiry */}
                      <td className="px-4 py-3">
                        <DateCell dateStr={aed.padsExpiry} />
                      </td>

                      {/* Last Inspected */}
                      <td className="px-4 py-3">
                        <div className="text-xs text-zinc-600">
                          {aed.lastInspected}
                        </div>
                        <div className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
                          <User size={10} />
                          {aed.inspector}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={aed.status} />
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setProfileModalAed(aed)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-zinc-200 bg-white text-zinc-700 text-xs font-semibold hover:bg-zinc-50 hover:border-zinc-400 transition-colors whitespace-nowrap"
                        >
                          <Eye size={12} />
                          View Profile
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <div className="px-4 py-2.5 border-t border-zinc-200 bg-zinc-50 text-xs text-zinc-400 flex items-center gap-1">
            <Info size={11} />
            Expiry dates shown in{" "}
            <span className="text-yellow-700 font-semibold">amber</span> are
            within 90 days.{" "}
            <span className="text-red-700 font-semibold">Red</span> indicates
            expired.
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <LogInspectionModal
        open={inspectionModalOpen}
        onClose={() => setInspectionModalOpen(false)}
        aeds={MOCK_AEDS}
      />
      <AedProfileModal
        open={!!profileModalAed}
        aed={profileModalAed}
        onClose={() => setProfileModalAed(null)}
      />
    </div>
  );
}
